<script setup lang="ts">
  import { useModal } from 'vue-final-modal';

  import Eye from '@/shared/assets/icons/EyeGray.svg';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import { formatCivilDateToPtBR, getNotificationStatusClasses, type NotificationResponse } from '@/shared/lib';
  import { ConfirmCloseModal, Checkbox, IconImage, Tooltip } from '@/shared/ui';

  import { getPeriodicityLabel } from '../model/periodicity';
  import NotificationCollaborators from './NotificationCollaborators.vue';
  import { NotificationStatus } from '@/shared/lib';

  const props = defineProps<{
    notification: NotificationResponse;
    isSelected: boolean;
    headers: string[];
  }>();

  const emit = defineEmits<{
    action: [action: string, id: number];
    'toggle-selection': [id: number];
  }>();

  const toggleNotification = (id: number) => {
    emit('toggle-selection', id);
  };

  const handleAction = (action: string) => {
    if (action === 'delete') {
      const { open: openConfirmModal, close: closeConfirmModal } = useModal({
        component: ConfirmCloseModal,
        attrs: {
          title: 'Tem certeza que deseja deletar a notificação?',
          description: 'A ação não pode ser desfeita.',
          onConfirm() {
            emit('action', 'delete', props.notification.id);
            closeConfirmModal();
          },
          onClose() {
            closeConfirmModal();
          },
        },
      });
      openConfirmModal();
    } else {
      emit('action', action, props.notification.id);
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case NotificationStatus[NotificationStatus.ACTIVE]:
        return 'Vigente';
      case NotificationStatus[NotificationStatus.CLOSED]:
        return 'Encerrada';
      case NotificationStatus[NotificationStatus.DRAFT]:
        return 'Rascunho';
      default:
        return 'Não definido';
    }
  };

  const getStatusEnum = (status: string): NotificationStatus => {
    switch (status) {
      case 'ACTIVE':
        return NotificationStatus.ACTIVE;
      case 'CLOSED':
        return NotificationStatus.CLOSED;
      case 'DRAFT':
        return NotificationStatus.DRAFT;
      default:
        return NotificationStatus.DRAFT;
    }
  };
</script>

<template>
  <tr class="table-row-hover border-t border-gray-02 transition-colors duration-300 ease-out">
    <td class="pl-6 align-middle text-center">
      <Checkbox
        :id="notification.id"
        :checked="isSelected"
        @toggle="toggleNotification(notification.id)"
      />
    </td>

    <td
      v-if="headers.includes('Notificações')"
      class="pl-2 py-4.5 text-sm text-gray-07 text-left"
    >
      {{ notification.title }}
    </td>

    <td
      v-if="headers.includes('Criado por')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
    >
      {{ notification.creator?.name }}
    </td>

    <td
      v-if="headers.includes('Destinatários')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
    >
      <NotificationCollaborators :recipients="notification.users?.data || []" />
    </td>

    <td
      v-if="headers.includes('Data de Início')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
    >
      {{ formatCivilDateToPtBR(notification.startDate) }}
    </td>

    <td
      v-if="headers.includes('Data Final')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
    >
      {{ formatCivilDateToPtBR(notification.endDate) }}
    </td>

    <td
      v-if="headers.includes('Periodicidade')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-left"
    >
      {{ getPeriodicityLabel(notification.periodicity, notification.customDays) }}
    </td>

    <td
      v-if="headers.includes('Status')"
      class="pl-6 py-4.5 text-sm text-gray-07 text-center"
    >
      <span :class="getNotificationStatusClasses(getStatusEnum(notification.status))">
        {{ getStatusLabel(notification.status) }}
      </span>
    </td>

    <td
      v-if="headers.includes('Ações')"
      class="px-6 py-4.5 text-sm text-gray-07 text-left"
    >
      <div class="flex gap-2 text-gray-xx-02">
        <Tooltip
          content="Visualizar"
          placement="top"
          class="flex items-center"
        >
          <button @click="handleAction('view')">
            <IconImage
              :src="Eye"
              alt="Olho"
            />
          </button>
        </Tooltip>
        <Tooltip
          v-if="!headers.includes('Criado por')"
          content="Editar"
          placement="top"
          class="flex items-center"
        >
          <button @click="handleAction('edit')">
            <IconImage
              :src="PencilIcon"
              alt="Editar"
            />
          </button>
        </Tooltip>
        <Tooltip
          content="Deletar"
          placement="top"
          class="flex items-center"
        >
          <button @click="handleAction('delete')">
            <IconImage
              :src="Trash"
              alt="Lixeira"
            />
          </button>
        </Tooltip>
      </div>
    </td>
  </tr>
</template>
